import * as Sentry from "@sentry/react";
import React, { Suspense } from 'react';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { ROUTES } from 'src/constants/routes';
import { getAuth } from 'src/redux/slices/auth';

import Protected from '../layout/protected';
import store from '../redux/store';
import { LogoutModal, useLogoutModal } from '../screens/shared/components/LogoutModal';
import getEnvironment from './getEnvironment';
import i18n from './i18n';

Sentry.init({
  dsn: "https://2db4bc0cc2aa8e4436c35a869c0391c4@o288530.ingest.sentry.io/4506638092337152",
  environment: getEnvironment(),
  integrations: [
    Sentry.browserTracingIntegration()
  ],
  tracesSampleRate: 1.0,
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const Inventory = React.lazy(() => import('../screens/inventory/Inventory'));
const Vehicle = React.lazy(() => import('../screens/vehicle/Vehicle'));
const Contacts = React.lazy(() => import('../screens/contacts/Contacts'));
const Account = React.lazy(() => import('../screens/account/Account'));
const Accounts = React.lazy(() => import('../screens/accounts/Accounts'));
const Analytics = React.lazy(() => import('../screens/analytics/Analytics'));
const ApiKeys = React.lazy(() => import('../screens/api_keys/ApiKeys'));
const NewDeal = React.lazy(() => import('../screens/new_deal/NewDeal'));
const Deals = React.lazy(() => import('../screens/deals/Deals'));
const Profile = React.lazy(() => import('../screens/profile/Profile'));
const Login = React.lazy(() => import('../screens/auth/login/Login'));
const RecoverPassword = React.lazy(() => import('../screens/auth/recover/Recover'));
const ChangePassword = React.lazy(() => import('../screens/auth/recover/password/Password'));

const ProtectedRoute = ({ Screen, ...rest }: any) => {
  const auth = useSelector(getAuth);
  const {
    openModalLogout,
    showModalLogout,
    handleOkModalLogout,
    handleCancelModalLogout,
    fetching,
    modalTitle: logoutTitle
  } = useLogoutModal({superAdminFullLogout: auth.superAdminUser && !auth.currentUser});
  
  const user = auth.superAdminUser && !auth.currentUser ? auth.superAdminUser : auth.currentUser;

  return user ? (
    <>
      <Protected
        logout={showModalLogout}
        user={user}
        pageTitle={rest.pageTitle}
      >
        <Screen {...rest} />
      </Protected>
      { openModalLogout && (
        <LogoutModal
          onOk={handleOkModalLogout}
          onCancel={handleCancelModalLogout}
          fetching={fetching}
          title={logoutTitle}
        />
      )}
    </>
  ) : (
    <Navigate to={ROUTES.LOGIN} />
  );
};

const publicPaths = [
  {
    exact: true,
    path: ROUTES.INVENTORY,
    element: <ProtectedRoute pageTitle={i18n.t('inventoryScreen:page_title')} Screen={Inventory} />,
  },
  {
    exact: true,
    path: ROUTES.CONTACTS,
    element: <ProtectedRoute pageTitle={i18n.t('contactsScreen:page_title')} Screen={Contacts} />,
  },
  {
    exact: true,
    path: ROUTES.PROFILE,
    element: <ProtectedRoute
      pageTitle={i18n.t('profileScreen:page_title')}
      Screen={Profile}
    />,
  },
  {
    exact: true,
    path: `${ROUTES.ACCOUNT}/:accountId?`,
    element: <ProtectedRoute
      pageTitle={i18n.t('accountScreen:page_title')}
      Screen={Account}
    />,
  },
  {
    exact: true,
    path: ROUTES.API_KEYS,
    element: <ProtectedRoute pageTitle={i18n.t('apiKeysScreen:page_title')} Screen={ApiKeys} />,
  },
  {
    exact: true,
    path: ROUTES.ACCOUNTS,
    element: <ProtectedRoute pageTitle={i18n.t('accountsScreen:page_title')} Screen={Accounts} />,
  },
  {
    exact: true,
    path: ROUTES.VEHICLE,
    element: (
      <ProtectedRoute pageTitle={i18n.t('inventoryScreen:page_title')} Screen={Vehicle} />
    ),
  },
  {
    exact: true,
    path: ROUTES.DEALS,
    element: (
      <ProtectedRoute pageTitle={i18n.t('dealsScreen:page_title')} Screen={Deals} />
    ),
  },
  {
    exact: true,
    path: ROUTES.NEW_DEAL,
    element: (
      <ProtectedRoute pageTitle={i18n.t('newDealScreen:page_title')} Screen={NewDeal} />
    ),
  },
  {
    exact: true,
    path: ROUTES.ANALYTICS,
    element: (
      <ProtectedRoute pageTitle={i18n.t('analyticsScreen:page_title')} Screen={Analytics} />
    ),
  },
  { exact: true, path: ROUTES.LOGIN, element: <Login /> },
  { exact: true, path: ROUTES.RECOVER, element: <RecoverPassword /> },
  { exact: true, path: ROUTES.CHANGE_PASSWORD, element: <ChangePassword /> },
  { exact: true, path: ROUTES.HOME, element: <Login /> },
];

const publicRoutes = publicPaths.map(({ path, ...props }) => (
  <Route key={path} path={path} {...props} />
));

const SuspenseFunction = () => (
  <Suspense fallback={<div />}>
    <Provider store={store}>
      <Router>
        <SentryRoutes>
          {publicRoutes}
        </SentryRoutes>
      </Router>
    </Provider>
  </Suspense>
);

export default SuspenseFunction;
